import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form } from "react-aria-components";
import { Button, EmptyState, Notice, SkeletonRows, TextField } from "@bres/ui";
import { api } from "../lib/api.ts";
import { errorText } from "../lib/errors.tsx";
import { useI18n } from "../lib/i18n.tsx";
import type { Me } from "../lib/session.tsx";
import { Login, LoginLayout } from "./Login.tsx";

interface Invitation { name: string; email: string; role: string; tenant_name: string; existing_account: boolean }

export function Invitation({ me }: { me: Me }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const invitation = useQuery({ queryKey: ["invitation", token], queryFn: () => api.get<Invitation>(`/v1/invitations/resolve?token=${encodeURIComponent(token)}`), retry: false });
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const accept = useMutation({
    mutationFn: () => api.post<{ createdAccount: boolean; tenantId: string }>("/v1/invitations/accept", { token, ...(invitation.data?.existing_account ? {} : { password }) }),
    onSuccess: async (result) => {
      if (result.createdAccount) {
        const res = await fetch("/v1/auth/sign-in/email", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email: invitation.data!.email, password }) });
        if (!res.ok) return window.location.assign("/");
      }
      window.location.assign("/");
    },
  });
  const signOut = useMutation({ mutationFn: () => api.post("/v1/auth/sign-out"), onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });
  if (invitation.isPending) return <LoginLayout><SkeletonRows rows={4} label={t("common.loading")} /></LoginLayout>;
  if (invitation.isError) return <LoginLayout><EmptyState icon={null} title={t("settings.team.invite_invalid")} actions={<Button onPress={() => window.location.assign("/")}>{t("login.submit")}</Button>}>{t("settings.team.invite_invalid_body")}</EmptyState></LoginLayout>;
  const item = invitation.data;
  if (item.existing_account && !me.authenticated) return <Login initialEmail={item.email} lead={t("settings.team.invite_signin", { site: item.tenant_name })} />;
  const wrongAccount = Boolean(item.existing_account && me.user?.email.toLowerCase() !== item.email.toLowerCase());
  return (
    <LoginLayout>
        <div className="login__brand"><h1>{t("settings.team.invite_accept_title", { site: item.tenant_name })}</h1><p>{item.name} · {item.email}</p></div>
        {wrongAccount ? <Notice tone="warning">{t("settings.team.invite_wrong_account")}<Button variant="ghost" isPending={signOut.isPending} onPress={() => signOut.mutate()}>{t("shell.sign_out")}</Button></Notice> : (
          <Form className="login__panel" onSubmit={(e) => { e.preventDefault(); if (item.existing_account || (password.length >= 12 && password === repeat)) accept.mutate(); }}>
            <p>{t("settings.team.invite_role", { role: t(`role.${item.role}`) })}</p>
            {!item.existing_account ? <>
              <TextField label={t("login.password")} type="password" autoComplete="new-password" value={password} onChange={setPassword} isRequired minLength={12} description={t("settings.team.invite_password_hint")} />
              <TextField label={t("settings.team.invite_password_repeat")} type="password" autoComplete="new-password" value={repeat} onChange={setRepeat} isRequired isInvalid={Boolean(repeat && repeat !== password)} errorMessage={t("settings.team.invite_password_mismatch")} />
            </> : null}
            {accept.isError ? <Notice tone="danger" role="alert">{errorText(t, accept.error)}</Notice> : null}
            <Button variant="primary" type="submit" block isPending={accept.isPending}>{t("settings.team.invite_accept")}</Button>
          </Form>
        )}
    </LoginLayout>
  );
}
